import React from 'react';
import { useEditor, pageMasterOf, masterLetter, scratchObjectsOf, spreadPartner } from '../../context/EditorContext';
import { flipTarget } from '../../lib/spreads';
import { nextCanvasUnit, CANVAS_UNIT_NAMES } from '../../lib/units';
import { Ruler } from './Ruler';
import { CanvasObject } from './CanvasObject';
import { WrapPointsEditor, WrapPointsBanner } from './WrapPointsEditor';
import { CropEditor, CropBanner } from './CropEditor';
import { EditPointsEditor, EditPointsBanner } from './EditPointsEditor';
import { PictureMiniToolbar } from './PictureMiniToolbar';
import { StoryMapOverlay, StoryMapPanel, useSelectedStory } from './StoryMap';
import { GuideLine } from './GuideLine';
import { GuideDragOverlay } from './GuideDragOverlay';
import { ObjectDragOverlay } from './ObjectDragOverlay';
import { panelGuidesOf } from '../../lib/foldPresets';
import { panelCenterGuidesOf } from '../../lib/panelFit';
import { loadUserFonts, ensureFonts, collectDocumentFonts, fontStatuses } from '../../lib/fontLoader';
import { SampleColorOverlay } from './SampleColorOverlay';
import { toast } from 'sonner';
import { X, Link2 } from 'lucide-react';
import { useStoryFlow } from '../../hooks/useStoryFlow';
import { documentObjects, locateObject, locationLabel } from '../../lib/storyChain';
import { StaticPage } from './ObjectRenderer';
import { fillToCss } from '../../lib/colors';
import { getBaselineGuide } from '../../lib/textLayout';
import { DEFAULT_TEXT_PROPS } from '../../context/EditorContext';
import { v4 as uuidv4 } from 'uuid';

export const CanvasWorkspace = () => {
  const { state, dispatch, rawDispatch, getCurrentPage, getActiveMaster, openDialog } = useEditor();
  const stateRefForFonts = React.useRef(state);
  stateRefForFonts.current = state;
  const containerRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const workspaceRef = React.useRef(null);

  const [isDrawing, setIsDrawing] = React.useState(false);
  const [drawStart, setDrawStart] = React.useState({ x: 0, y: 0 });
  const [drawRect, setDrawRect] = React.useState(null);
  const [cursorPosition, setCursorPosition] = React.useState({ x: null, y: null });
  const [alignmentGuides, setAlignmentGuides] = React.useState([]);
  const [marquee, setMarquee] = React.useState(null);

  const currentPage = getCurrentPage();
  const master = getActiveMaster();
  const isMasterView = state.viewMode === 'master';
  // Scratch area (Publisher): objects parked beside the page belong to the publication and show next to *every* page.
  // In Normal view they are edited exactly like page objects, so every hit-test / render list below uses `editableObjects`.
  const scratchStored = scratchObjectsOf(state);
  const scratchObjects = React.useMemo(() => (isMasterView ? [] : scratchStored), [isMasterView, scratchStored]);
  const scratchIds = React.useMemo(() => new Set(scratchObjects.map((o) => o.id)), [scratchObjects]);
  const editableObjects = React.useMemo(() => (isMasterView ? master?.objects || [] : [...(currentPage?.objects || []), ...scratchObjects]), [isMasterView, master, currentPage, scratchObjects]);
  // Stories link across pages: the Story Map sees the whole publication, and boxes on other pages are measured off-screen.
  const docObjects = React.useMemo(() => (isMasterView ? master?.objects || [] : documentObjects(state.pages, state.scratch)), [isMasterView, master, state.pages, state.scratch]);
  const renderedIds = React.useMemo(() => new Set(editableObjects.map((o) => o.id)), [editableObjects]);
  useStoryFlow(renderedIds);
  const vo = state.viewOptions || {};
  const WORKSPACE_PADDING = vo.scratchArea === false ? 40 : 288;
  const showRulers = vo.rulers !== false;

  /* ---------- track viewport size so the page can be centered ---------- */
  const [viewport, setViewport] = React.useState({ w: 0, h: 0 });
  React.useEffect(() => {
    const ws = workspaceRef.current;
    if (!ws) return undefined;
    const update = () => setViewport({ w: ws.clientWidth, h: ws.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(ws);
    return () => ro.disconnect();
  }, []);

  /* ---------- zoom (ctrl+wheel) ---------- */
  const handleWheel = React.useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      dispatch({ type: 'SET_ZOOM', payload: Math.max(0.1, Math.min(6, state.zoom + (e.deltaY > 0 ? -0.1 : 0.1))) });
    }
  }, [dispatch, state.zoom]);
  React.useEffect(() => {
    const c = containerRef.current;
    if (!c) return undefined;
    c.addEventListener('wheel', handleWheel, { passive: false });
    return () => c.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  /* ---------- zoom modes ---------- */
  const zoomTo = React.useCallback((mode) => {
    const ws = workspaceRef.current;
    if (!ws) return;
    const availW = ws.clientWidth - 48;
    const availH = ws.clientHeight - 48;
    const pagesWide = state.layoutMode === 'spread' ? 2 : 1;
    let z = state.zoom;
    if (mode === 'page') z = Math.min(availW / (currentPage.width * pagesWide + (pagesWide - 1) * 24), availH / currentPage.height);
    else if (mode === 'width') z = availW / (currentPage.width * pagesWide + (pagesWide - 1) * 24);
    else if (mode === 'selection') {
      const objs = editableObjects.filter((o) => state.selectedObjectIds.includes(o.id));
      if (!objs.length) return;
      const minX = Math.min(...objs.map((o) => o.x)), minY = Math.min(...objs.map((o) => o.y));
      const maxX = Math.max(...objs.map((o) => o.x + o.width)), maxY = Math.max(...objs.map((o) => o.y + o.height));
      z = Math.min(availW / Math.max(1, maxX - minX), availH / Math.max(1, maxY - minY), 6) * 0.9;
      dispatch({ type: 'SET_ZOOM', payload: z });
      requestAnimationFrame(() => {
        ws.scrollLeft = WORKSPACE_PADDING + minX * z - (availW - (maxX - minX) * z) / 2;
        ws.scrollTop = WORKSPACE_PADDING + minY * z - (availH - (maxY - minY) * z) / 2;
      });
      return;
    } else if (typeof mode === 'number') z = mode;
    dispatch({ type: 'SET_ZOOM', payload: Math.max(0.1, Math.min(6, z)) });
    requestAnimationFrame(() => { ws.scrollLeft = Math.max(0, WORKSPACE_PADDING - 24); ws.scrollTop = Math.max(0, WORKSPACE_PADDING - 24); });
  }, [state.zoom, state.layoutMode, state.selectedObjectIds, currentPage, editableObjects, dispatch, WORKSPACE_PADDING]);

  React.useEffect(() => {
    const handler = (e) => zoomTo(e.detail);
    window.addEventListener('wp:zoom', handler);
    return () => window.removeEventListener('wp:zoom', handler);
  }, [zoomTo]);

  // Initial: fit page to window (and again whenever a project is loaded / created). zoomTo changes identity with every
  // zoom / selection change, so these run on their own triggers only and call the latest zoomTo through a ref.
  const zoomToRef = React.useRef(zoomTo);
  React.useEffect(() => { zoomToRef.current = zoomTo; }, [zoomTo]);
  const didInit = React.useRef(false);
  React.useEffect(() => {
    if (!didInit.current && workspaceRef.current) {
      didInit.current = true;
      setTimeout(() => zoomToRef.current('page'), 50);
    }
  }, []);
  React.useEffect(() => {
    if (state.loadSeq) { const t = setTimeout(() => zoomToRef.current('page'), 60); return () => clearTimeout(t); }
    return undefined;
  }, [state.loadSeq]);

  /* ---------- helpers ---------- */
  const getCanvasPosition = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / state.zoom, y: (e.clientY - rect.top) / state.zoom };
  };

  const handleMouseMove = (e) => {
    if (workspaceRef.current) {
      const rect = workspaceRef.current.getBoundingClientRect();
      setCursorPosition({ x: e.clientX - rect.left + workspaceRef.current.scrollLeft, y: e.clientY - rect.top + workspaceRef.current.scrollTop });
    }
    if (isDrawing && canvasRef.current) {
      const pos = getCanvasPosition(e);
      setDrawRect({ x: Math.min(pos.x, drawStart.x), y: Math.min(pos.y, drawStart.y), width: Math.abs(pos.x - drawStart.x), height: Math.abs(pos.y - drawStart.y) });
    }
    if (marquee) {
      const pos = getCanvasPosition(e);
      setMarquee({ ...marquee, x2: pos.x, y2: pos.y });
    }
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const target = e.target;
    // Clicks inside floating UI never count as "empty canvas": guides, facing page, the point editors + their banners,
    // the picture mini toolbar and any Radix menu / popover (portaled content still bubbles through the React tree).
    if (target.closest('.guide-line') || target.closest('[data-spread-page]') || target.closest('[data-testid="wrap-points-editor"], [data-testid="wrap-points-banner"], [data-testid="edit-points-editor"], [data-testid="edit-points-banner"], [data-testid="selection-mini-toolbar"], [role="menu"], [data-radix-popper-content-wrapper], [data-radix-menu-content]')) return;
    // Drawing tools may start on top of existing objects (Publisher lets you draw anywhere on the page).
    const drawing = ['shape', 'text', 'table', 'picture-placeholder'].includes(state.activeTool);
    if (target.closest('[data-testid^="canvas-object-"]') && !drawing) return;
    const isCanvasArea = canvasRef.current?.contains(target) || target === canvasRef.current;
    if (state.pendingLink) rawDispatch({ type: 'SET_PENDING_LINK', payload: null });
    if (state.activeTool === 'select') {
      dispatch({ type: 'DESELECT_ALL' });
      const pos = getCanvasPosition(e);
      setMarquee({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
      return;
    }
    if (['shape', 'text', 'table', 'picture-placeholder'].includes(state.activeTool) && (isCanvasArea || workspaceRef.current?.contains(target))) {
      const pos = getCanvasPosition(e);
      setIsDrawing(true);
      setDrawStart(pos);
      setDrawRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    }
  };

  // Double-clicking a master-page object in Normal view (they are not selectable there, like Publisher) explains where
  // to edit it and offers to open the master page — the tester could not find how to edit / delete master items.
  const handleDoubleClick = (e) => {
    if (isMasterView || state.activeTool !== 'select' || !pageMaster) return;
    if (e.target.closest('[data-testid^="canvas-object-"]:not([data-master-object])')) return;
    const pos = getCanvasPosition(e);
    const hit = [...masterObjects].reverse().find((o) => pos.x >= o.x && pos.x <= o.x + o.width && pos.y >= o.y && pos.y <= o.y + o.height);
    if (!hit) return;
    toast.info(`That is a master page object (${pageMaster.name})`, {
      id: 'master-object-hint',
      description: 'It repeats on every page using this master. Open the master page to move, edit or delete it.',
      action: { label: 'Edit Master Page', onClick: () => dispatch({ type: 'EDIT_MASTER', payload: { id: pageMaster.id } }) },
      duration: 6000,
    });
  };

  const handleMouseUp = () => {
    if (marquee) {
      const x0 = Math.min(marquee.x1, marquee.x2), x1 = Math.max(marquee.x1, marquee.x2);
      const y0 = Math.min(marquee.y1, marquee.y2), y1 = Math.max(marquee.y1, marquee.y2);
      if (x1 - x0 > 4 && y1 - y0 > 4) {
        const hit = editableObjects.filter((o) => o.visible !== false && !o.locked && o.x < x1 && o.x + o.width > x0 && o.y < y1 && o.y + o.height > y0).map((o) => o.id);
        if (hit.length) dispatch({ type: 'SELECT_OBJECTS', payload: hit });
      }
      setMarquee(null);
    }
    if (isDrawing && drawRect) {
      const drew = drawRect.width > 8 && drawRect.height > 8;
      const w = drew ? drawRect.width : state.activeTool === 'text' ? 240 : 160;
      const h = drew ? drawRect.height : state.activeTool === 'text' ? 80 : 120;
      const base = { x: drawRect.x, y: drawRect.y, width: w, height: h, rotation: 0 };
      if (state.activeTool === 'shape') {
        dispatch({ type: 'ADD_OBJECT', payload: { type: 'shape', shapeType: state.activeShape, ...base, properties: { fill: { ...(state.shapeSettings?.fill || { type: 'solid', color: '#7c3aed' }) }, stroke: { ...(state.shapeSettings?.stroke || { color: '#000000', width: 0 }) }, cornerRadius: state.activeShape === 'rounded-rect' ? 12 : 0, opacity: 100, wrapStyle: 'none' } } });
      } else if (state.activeTool === 'text') {
        // Publisher behaviour: a freshly drawn text box is immediately ready for typing.
        const id = uuidv4();
        dispatch({ type: 'ADD_OBJECT', payload: { id, type: 'textbox', ...base, properties: { ...DEFAULT_TEXT_PROPS, text: '', fontFamily: state.textSettings?.fontFamily || 'Inter', fontSize: state.textSettings?.fontSize || 16 } } });
        setTimeout(() => rawDispatch({ type: 'SET_EDITING', payload: id }), 0);
      } else if (state.activeTool === 'table') {
        const rows = state.pendingTable?.rows || 3, cols = state.pendingTable?.cols || 3;
        dispatch({ type: 'ADD_OBJECT', payload: { type: 'table', ...base, width: drew ? w : 320, height: drew ? h : rows * 32, properties: { rows, cols, cells: Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ text: '' }))), fontFamily: 'Inter', fontSize: 14, color: '#000000', borderColor: '#000000', borderWidth: 1, headerRow: true, cellPadding: 4, fill: { type: 'none' }, wrapStyle: 'none' } } });
      } else if (state.activeTool === 'picture-placeholder') {
        dispatch({ type: 'ADD_OBJECT', payload: { type: 'image', ...base, width: drew ? w : 240, height: drew ? h : 180, properties: { src: '', placeholder: true, opacity: 100, borderRadius: 0, objectFit: 'cover', wrapStyle: 'square' } } });
      }
      dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' });
    }
    setIsDrawing(false);
    setDrawRect(null);
  };

  const handleMouseLeave = () => {
    if (isDrawing) { setIsDrawing(false); setDrawRect(null); }
    setMarquee(null);
    setCursorPosition({ x: null, y: null });
  };

  /* ---------- keyboard shortcuts ---------- */
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      const t = e.target;
      const k0 = e.key.toLowerCase();
      // Find / Replace work from anywhere (including while editing text), like Publisher.
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (k0 === 'f' || k0 === 'h') && !t.closest?.('[role="dialog"]:not([data-testid="find-replace-pane"])')) {
        e.preventDefault();
        rawDispatch({ type: 'SET_FIND_PANE', payload: { mode: k0 === 'h' ? 'replace' : 'find' } });
        return;
      }
      // Show All (special characters) toggles from anywhere, including while editing text — Publisher's Ctrl+Shift+Y.
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && k0 === 'y' && !t.closest?.('[role="dialog"]')) {
        e.preventDefault();
        rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'specialChars', value: !stateRefForFonts.current.viewOptions?.specialChars } });
        return;
      }
      // Print / Save also work while editing text (they open the File tab).
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (k0 === 'p' || k0 === 's') && (t.isContentEditable || t.closest?.('[data-testid="find-replace-pane"]'))) {
        e.preventDefault();
        openDialog(k0 === 'p' ? 'print-project' : e.shiftKey ? 'save-as-project' : 'save-project');
        return;
      }
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || t.closest?.('[role="dialog"]')) return;
      // Keys typed while a ribbon menu / popover / listbox is open belong to that menu (typeahead, Escape closes it).
      if (t.closest?.('[data-radix-popper-content-wrapper], [role="menu"], [role="listbox"]')) return;
      const key = e.key.toLowerCase();
      // Escape belongs to an open menu / popover / dialog (Radix closes it); never let it also deselect canvas objects.
      if (key === 'escape' && document.querySelector('[data-radix-popper-content-wrapper], [role="dialog"]:not([data-testid="find-replace-pane"])')) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const sel = state.selectedObjectIds;
      switch (key) {
        case 'v': if (ctrl) dispatch({ type: 'PASTE_OBJECTS' }); else dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' }); break;
        case 't': if (!ctrl) dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'text' }); break;
        case 's': if (!ctrl) dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'shape' }); else { e.preventDefault(); openDialog(e.shiftKey ? 'save-as-project' : 'save-project'); } break;
        case 'o': if (ctrl) { e.preventDefault(); openDialog('open-project'); } break;
        case 'p': if (ctrl) { e.preventDefault(); openDialog('print-project'); } break;
        case 'delete': case 'backspace': if (sel.length) { e.preventDefault(); dispatch({ type: 'DELETE_OBJECTS', payload: sel }); } break;
        case 'escape':
          // Publisher: the first Esc switches the Format Painter off / leaves "Edit Wrap Points" / "Edit Points" but keeps the object selected.
          if (state.formatPainter) { rawDispatch({ type: 'SET_FORMAT_PAINTER', payload: null }); break; }
          if (state.cropEditId) { rawDispatch({ type: 'SET_CROP_EDIT', payload: null }); break; }
          if (state.wrapEditId) { rawDispatch({ type: 'SET_WRAP_EDIT', payload: null }); break; }
          if (state.pointEditId) { rawDispatch({ type: 'SET_POINT_EDIT', payload: null }); break; }
          dispatch({ type: 'DESELECT_ALL' }); dispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' }); rawDispatch({ type: 'SET_PENDING_LINK', payload: null }); break;
        case 'c': if (ctrl) dispatch({ type: 'COPY_OBJECTS' }); break;
        case 'x': if (ctrl) dispatch({ type: 'CUT_OBJECTS' }); break;
        case 'd': if (ctrl) { e.preventDefault(); dispatch({ type: 'DUPLICATE_OBJECTS' }); } break;
        case 'a': if (ctrl) { e.preventDefault(); dispatch({ type: 'SELECT_ALL' }); } break;
        case 'k': if (ctrl && sel.length === 1) { e.preventDefault(); openDialog('hyperlink', { objectId: sel[0] }); } break;
        case 'g': if (ctrl) { e.preventDefault(); dispatch({ type: e.shiftKey ? 'UNGROUP_OBJECTS' : 'GROUP_OBJECTS' }); } break;
        case '0': if (ctrl) { e.preventDefault(); dispatch({ type: 'SET_ZOOM', payload: 1 }); } break;
        case '1': if (ctrl) { e.preventDefault(); zoomTo('page'); } break;
        case 'f': if (ctrl && e.shiftKey) { e.preventDefault(); dispatch({ type: 'BRING_TO_FRONT' }); } break;
        case 'b': if (ctrl && e.shiftKey) { e.preventDefault(); dispatch({ type: 'SEND_TO_BACK' }); } break;
        case 'arrowup': case 'arrowdown': case 'arrowleft': case 'arrowright': {
          if (!sel.length) break;
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          const dx = key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0;
          const dy = key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0;
          const objs = editableObjects.filter((o) => sel.includes(o.id));
          dispatch({ type: 'UPDATE_OBJECTS', payload: objs.map((o) => ({ id: o.id, data: { x: o.x + dx, y: o.y + dy } })) });
          break;
        }
        // Page Down / Page Up flip through the booklet: a whole spread in Two-Page Spread view (landing on its left-hand
        // page), one page in Single Page view; Shift steps a single page either way (switches to the facing page);
        // Home / End jump to the first / last page. preventDefault keeps the workspace from scrolling as well.
        case 'pageup': case 'pagedown': {
          if (isMasterView || state.pages.length < 2) break;
          e.preventDefault();
          const dir = key === 'pagedown' ? 1 : -1;
          const target = flipTarget(state.currentPageIndex, state.pages.length, dir, state.layoutMode === 'spread' && !e.shiftKey);
          if (target !== state.currentPageIndex) dispatch({ type: 'SET_CURRENT_PAGE', payload: target });
          break;
        }
        case 'home': case 'end': {
          if (isMasterView || state.pages.length < 2 || sel.length) break;
          e.preventDefault();
          const target = key === 'home' ? 0 : state.pages.length - 1;
          if (target !== state.currentPageIndex) dispatch({ type: 'SET_CURRENT_PAGE', payload: target });
          break;
        }
        default: break;
      }
    };
    // Capture phase: runs before Radix's document-level Escape handling, so an open menu/popover is still in the DOM.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [dispatch, rawDispatch, state.selectedObjectIds, state.currentPageIndex, state.pages.length, state.layoutMode, state.wrapEditId, state.pointEditId, state.cropEditId, state.formatPainter, editableObjects, isMasterView, openDialog, zoomTo]);

  /* ---------- derived ---------- */
  let cursorStyle = 'default';
  if (state.activeTool === 'text') cursorStyle = 'text';
  else if (state.activeTool !== 'select') cursorStyle = 'crosshair';
  if (state.pendingLink) cursorStyle = 'copy';

  const activeObjects = editableObjects.filter((o) => o.visible !== false).sort((a, b) => a.layer - b.layer);
  // Normal view shows the current page's master underneath (none when the page ignores its master)
  const pageMaster = !isMasterView ? pageMasterOf(state, currentPage) : null;
  const masterObjects = pageMaster ? pageMaster.objects.filter((o) => o.visible !== false).sort((a, b) => a.layer - b.layer) : [];
  const wrapEditTarget = state.wrapEditId ? activeObjects.find((o) => o.id === state.wrapEditId) || null : null;
  const cropEditTarget = state.cropEditId ? activeObjects.find((o) => o.id === state.cropEditId) || null : null;
  // Baseline grid: saved with the publication as a special guide entry (the EditorProvider syncs it to the layout engine).
  const baselineGuide = React.useMemo(() => getBaselineGuide(state.guides, vo.baselineSpacing), [state.guides, vo.baselineSpacing]);
  // Ruler guides + the panel-margin guides derived from fold lines (rendered and snapped to like any other guide)
  // Fonts: register the user's uploaded fonts once, and load (from Google Fonts) every family this publication refers to,
  // so imported / opened documents render in their real faces instead of the fallback serif.
  React.useEffect(() => { loadUserFonts(); }, []);
  React.useEffect(() => {
    const t = window.setTimeout(() => { ensureFonts(collectDocumentFonts(state.pages, state.masters, state.scratch)); }, 250);
    return () => window.clearTimeout(t);
  }, [state.pages, state.masters, state.scratch]);
  // After a publication is opened / imported: tell the user which of its fonts are not available here and offer to replace them.
  const loadSeq = state.loadSeq || 0;
  React.useEffect(() => {
    if (!loadSeq) return undefined;
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const statuses = await fontStatuses(collectDocumentFonts(stateRefForFonts.current.pages, stateRefForFonts.current.masters));
      const missing = statuses.filter((f) => f.status === 'missing').map((f) => f.family);
      if (cancelled || !missing.length) return;
      toast.warning(`${missing.length === 1 ? 'A font is' : `${missing.length} fonts are`} not available here: ${missing.slice(0, 4).join(', ')}${missing.length > 4 ? '…' : ''}`, {
        id: 'missing-fonts',
        description: 'Text in these fonts is shown in a substitute face. Replace them across the publication or upload the font files.',
        duration: 12000,
        action: { label: 'Replace fonts…', onClick: () => openDialog('font-substitution', { fonts: missing }) },
      });
    }, 900);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [loadSeq, openDialog]);
  // First time an object is parked beside the page in this session: explain the scratch area (once, Publisher users expect it, others don't).
  const prevScratchCount = React.useRef(scratchObjects.length);
  const prevLoadSeq = React.useRef(loadSeq);
  React.useEffect(() => {
    const grew = scratchObjects.length > prevScratchCount.current && prevLoadSeq.current === loadSeq;
    prevScratchCount.current = scratchObjects.length;
    prevLoadSeq.current = loadSeq;
    if (!grew || isMasterView) return;
    try {
      if (window.sessionStorage.getItem('wp-scratch-hint')) return;
      window.sessionStorage.setItem('wp-scratch-hint', '1');
    } catch { /* storage unavailable: show the hint anyway */ }
    toast.info('Moved to the scratch area', { id: 'scratch-hint', description: 'Objects beside the page belong to the publication: they show next to every page and are never printed. Drag one onto a page to make it part of that page.', duration: 7000 });
  }, [scratchObjects.length, loadSeq, isMasterView]);
  // After a drag ends with the object straddling the page edge, explain once that it still belongs to this page
  // (only an object dragged *fully* off the page becomes shared) — the single most common scratch-area surprise.
  const prevInteracting = React.useRef(state.interacting);
  React.useEffect(() => {
    const was = prevInteracting.current;
    prevInteracting.current = state.interacting;
    if (!was || state.interacting || isMasterView || !currentPage) return;
    const o = (currentPage.objects || []).find((x) => x.id === was);
    if (!o) return;
    const outside = o.x < 0 || o.y < 0 || o.x + o.width > currentPage.width || o.y + o.height > currentPage.height;
    const entirely = o.x + o.width <= 0 || o.y + o.height <= 0 || o.x >= currentPage.width || o.y >= currentPage.height;
    if (!outside || entirely) return;
    try {
      if (window.sessionStorage.getItem('wp-straddle-hint')) return;
      window.sessionStorage.setItem('wp-straddle-hint', '1');
    } catch { /* storage unavailable: show the hint anyway */ }
    toast.info(`Still part of ${currentPage.name}`, { id: 'scratch-hint', description: 'An object that overlaps the page stays on that page (the part outside is not printed). Drag it fully into the grey area to show it beside every page.', duration: 7000 });
  }, [state.interacting, currentPage, isMasterView]);
  const showPanelCenters = state.viewOptions?.panelCenters !== false;
  const lineGuides = React.useMemo(() => {
    const stored = state.guides.filter((g) => g.kind !== 'baseline' && g.orientation);
    return [...stored, ...panelGuidesOf(state.guides, currentPage), ...(showPanelCenters ? panelCenterGuidesOf(state.guides, currentPage) : [])];
  }, [state.guides, currentPage, showPanelCenters]);
  const pointEditTarget = state.pointEditId ? activeObjects.find((o) => o.id === state.pointEditId) || null : null;
  // Picture mini toolbar: a single, unlocked picture selected in Normal view, no edit mode active, no dialog / modal on top
  // Story Map: the linked chain of the selected text box (badges + threads on the page, list panel in the corner)
  const story = useSelectedStory(docObjects);
  const miniToolbarTarget = vo.miniToolbar !== false && !isMasterView && !wrapEditTarget && !pointEditTarget && !cropEditTarget && !state.dialog && !state.fileOpen && !state.showPhotoEditor && !state.showImageGallery && state.selectedObjectIds.length === 1 && state.activeTool === 'select'
    ? activeObjects.find((o) => o.id === state.selectedObjectIds[0] && o.type === 'image' && !o.locked) || null
    : null;

  const canvasWidth = currentPage.width * state.zoom;
  const canvasHeight = currentPage.height * state.zoom;
  const spread = state.layoutMode === 'spread' && !isMasterView && state.pages.length > 1;
  // Joined sheet (View ▸ Joined Sheet): the facing pages sit edge to edge as the one sheet they will be printed on, with
  // the fold line down the middle — no gap, one shadow. Separate pages (default) keep a small gap between the two.
  const joined = spread && state.spreadStyle === 'joined';
  const spreadGap = joined ? 0 : 24 * state.zoom;
  // Facing pages, booklet-style like Publisher: page 1 alone on the right, then (2,3) (4,5) …, an even last page alone on
  // the left. 0-based: index 0 → right, odd index → left page of a spread, even index > 0 → right page of a spread.
  const { facingIndex, currentOnRight } = spreadPartner(state.currentPageIndex, state.pages.length, spread);
  const facingPage = facingIndex !== null ? state.pages[facingIndex] : null;
  // page 1 keeps the empty left half of its spread, an even last page keeps its empty right half — where Publisher puts them
  const ghostLeftW = spread && facingIndex === null && currentOnRight ? currentPage.width * state.zoom + spreadGap : 0;
  const ghostRightW = spread && facingIndex === null && !currentOnRight && state.currentPageIndex > 0 ? currentPage.width * state.zoom + spreadGap : 0;
  const facingW = facingPage ? facingPage.width * state.zoom + spreadGap : ghostLeftW || ghostRightW;
  const contentW = canvasWidth + facingW + WORKSPACE_PADDING * 2;
  const contentH = canvasHeight + WORKSPACE_PADDING * 2;
  // Center the page(s) when the content is narrower / shorter than the viewport (Publisher-like)
  const centerX = Math.max(0, (viewport.w - contentW) / 2);
  const centerY = Math.max(0, (viewport.h - contentH) / 2);
  const PAD_TOP = WORKSPACE_PADDING + centerY;
  const canvasLeft = centerX + WORKSPACE_PADDING + (currentOnRight ? facingW : 0);
  const totalWidth = canvasLeft + canvasWidth + ((facingPage && !currentOnRight) || ghostRightW ? facingW : 0) + WORKSPACE_PADDING;
  const totalHeight = PAD_TOP + canvasHeight + WORKSPACE_PADDING;
  // the joined sheet: both halves (a blank half included) and the fold between them
  const sheetLeft = centerX + WORKSPACE_PADDING;
  const sheetWidth = canvasWidth + facingW;
  const foldX = currentOnRight ? canvasLeft : canvasLeft + canvasWidth;
  const pageShadow = '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -1px rgba(0,0,0,.06), 0 25px 50px -12px rgba(0,0,0,.25)';
  const pageBg = fillToCss(currentPage.background, '#ffffff');
  const bgImage = currentPage.background?.type === 'image' && currentPage.background.image?.url
    ? { backgroundImage: `url(${currentPage.background.image.url})`, backgroundSize: currentPage.background.image.fit === 'tile' ? 'auto' : 'cover', backgroundRepeat: currentPage.background.image.fit === 'tile' ? 'repeat' : 'no-repeat', backgroundPosition: 'center' }
    : {};

  const rulerBox = (
    <div className="w-6 h-6 bg-muted border-b border-r border-border flex items-center justify-center flex-shrink-0 cursor-pointer" onClick={() => dispatch({ type: 'SET_CANVAS_UNIT', payload: nextCanvasUnit(state.canvasUnit) })} title={`Ruler units: ${CANVAS_UNIT_NAMES[state.canvasUnit] || state.canvasUnit} — click to change (in → cm → pt → px)`} data-testid="ruler-unit-corner">
      <span className="text-[8px] text-muted-foreground uppercase">{state.canvasUnit}</span>
    </div>
  );

  return (
    <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden bg-background min-w-0" data-testid="canvas-workspace">
      {isMasterView && (
        <div className="min-h-8 py-1 bg-[hsl(var(--wp-context-amber))]/15 text-foreground text-xs flex items-center justify-between gap-3 px-3 border-b border-[hsl(var(--wp-context-amber))]/50" data-testid="master-page-banner">
          <span className="flex items-center gap-2 min-w-0">
            <span className="inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-sm bg-[hsl(var(--wp-context-amber))] text-[11px] font-bold text-white" aria-hidden="true">{masterLetter(state.masters, master?.id)}</span>
            <span className="font-semibold truncate" data-testid="master-page-banner-name">Editing {master?.name || 'Master Page'}</span>
            <span className="text-muted-foreground hidden md:inline truncate">— objects here repeat on the {state.pages.filter((p) => p.masterId === master?.id).length} page{state.pages.filter((p) => p.masterId === master?.id).length === 1 ? '' : 's'} using this master. Add, move or delete them like any other object.</span>
          </span>
          <button type="button" className="shrink-0 h-6 px-2.5 rounded-sm bg-[hsl(var(--wp-context-amber))] text-white text-[11px] font-semibold hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring inline-flex items-center gap-1" onClick={() => dispatch({ type: 'SET_VIEW_MODE', payload: 'normal' })} data-testid="close-master-view"><X className="h-3 w-3" aria-hidden="true" /> Close Master Page</button>
        </div>
      )}
      {state.pendingLink && !isMasterView && (() => {
        const loc = locateObject(state.pendingLink, state.pages, state.scratch);
        const here = !loc || loc.scratch || loc.pageIndex === state.currentPageIndex;
        return (
          <div className="min-h-8 py-1 bg-primary/10 text-foreground text-xs flex items-center justify-between gap-3 px-3 border-b border-primary/40" data-testid="link-mode-banner">
            <span className="flex items-center gap-2 min-w-0">
              <Link2 className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
              <span className="font-semibold truncate">Linking{loc && !here ? ` from a text box on ${locationLabel(loc, state.pages)}` : ''}</span>
              <span className="text-muted-foreground truncate">— click an empty text box to continue the story there. Switch pages with the Pages pane; Esc cancels.</span>
            </span>
            <button type="button" className="shrink-0 h-6 px-2.5 rounded-sm border border-primary/40 text-primary text-[11px] font-semibold hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring inline-flex items-center gap-1" onClick={() => rawDispatch({ type: 'SET_PENDING_LINK', payload: null })} data-testid="link-mode-cancel"><X className="h-3 w-3" aria-hidden="true" /> Cancel</button>
          </div>
        );
      })()}
      {showRulers && (
        <div className="flex flex-shrink-0">
          {rulerBox}
          <div className="flex-1 overflow-hidden">
            <Ruler orientation="horizontal" length={currentPage.width} zoom={state.zoom} scrollOffset={workspaceRef.current?.scrollLeft || 0} unit={state.canvasUnit} cursorPosition={cursorPosition.x} workspacePadding={canvasLeft} />
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden relative">
        {showRulers && (
          <div className="flex-shrink-0 overflow-hidden">
            <Ruler orientation="vertical" length={currentPage.height} zoom={state.zoom} scrollOffset={workspaceRef.current?.scrollTop || 0} unit={state.canvasUnit} cursorPosition={cursorPosition.y} workspacePadding={PAD_TOP} />
          </div>
        )}
        {wrapEditTarget && <WrapPointsBanner object={wrapEditTarget} />}
        {cropEditTarget && <CropBanner object={cropEditTarget} />}
        {pointEditTarget && <EditPointsBanner object={pointEditTarget} />}
        <div
          ref={workspaceRef}
          className={`flex-1 overflow-auto relative ${vo.scratchArea === false ? 'bg-muted/40' : 'workspace-grid'}`}
          style={{ cursor: cursorStyle }}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onScroll={() => setCursorPosition((prev) => ({ ...prev }))}
          data-testid="editor-canvas"
        >
          <div className="relative" style={{ width: totalWidth, height: totalHeight, minWidth: '100%', minHeight: '100%' }}>
            {/* joined sheet: one continuous sheet under both facing pages (and under a blank half), one shadow */}
            {joined && (
              <div className="absolute pointer-events-none" style={{ left: sheetLeft, top: PAD_TOP, width: sheetWidth, height: canvasHeight, background: '#ffffff', boxShadow: pageShadow }} aria-hidden="true" data-testid="spread-joined-sheet" data-fold-x={Math.round(foldX - sheetLeft)} />
            )}
            {/* page 1 of a booklet has no facing page: keep its empty left half (Publisher does the same) */}
            {ghostLeftW > 0 && (
              <div className={`absolute rounded-sm border border-dashed border-border/70 pointer-events-none ${joined ? 'bg-muted/30' : ''}`} style={{ left: centerX + WORKSPACE_PADDING, top: PAD_TOP, width: currentPage.width * state.zoom, height: canvasHeight }} aria-hidden="true" data-testid="spread-empty-slot" />
            )}
            {/* an even last page sits alone on the left: keep its empty right half */}
            {ghostRightW > 0 && (
              <div className={`absolute rounded-sm border border-dashed border-border/70 pointer-events-none ${joined ? 'bg-muted/30' : ''}`} style={{ left: canvasLeft + canvasWidth + spreadGap, top: PAD_TOP, width: currentPage.width * state.zoom, height: canvasHeight }} aria-hidden="true" data-testid="spread-empty-slot" />
            )}
            {/* facing page (spread) */}
            {facingPage && (
              <div
                className="absolute cursor-pointer group"
                style={{ left: currentOnRight ? centerX + WORKSPACE_PADDING : canvasLeft + canvasWidth + spreadGap, top: PAD_TOP, width: facingPage.width * state.zoom, height: facingPage.height * state.zoom, boxShadow: joined ? 'none' : '0 4px 6px -1px rgba(0,0,0,.1), 0 25px 50px -12px rgba(0,0,0,.25)' }}
                onClick={() => dispatch({ type: 'SET_CURRENT_PAGE', payload: facingIndex })}
                title={`Click to edit ${facingPage.name}`}
                data-spread-page
                data-testid="spread-facing-page"
              >
                <StaticPage page={facingPage} master={pageMasterOf(state, facingPage)} scale={state.zoom} textFlow={state.textFlow} />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors" />
                <div className={`absolute -bottom-6 text-[11px] text-muted-foreground ${joined && currentOnRight ? 'left-0' : joined ? 'right-0' : 'left-0'}`}>{facingPage.name} (click to edit)</div>
              </div>
            )}

            {/* current page */}
            <div
              ref={canvasRef}
              className="canvas-page absolute"
              style={{ left: canvasLeft, top: PAD_TOP, width: canvasWidth, height: canvasHeight, background: pageBg, ...bgImage, boxShadow: joined ? 'none' : pageShadow, outline: isMasterView ? '2px dashed hsl(38 92% 50%)' : undefined }}
              data-testid="canvas-page"
            >
              {state.gridVisible && (
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)', backgroundSize: `${state.gridSpacing * state.zoom}px ${state.gridSpacing * state.zoom}px` }} />
              )}
              {vo.baselines && (
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(56,189,248,0.45) 1px, transparent 1px)', backgroundSize: `100% ${baselineGuide.spacing * state.zoom}px`, backgroundPosition: `0 ${(baselineGuide.offset % baselineGuide.spacing) * state.zoom}px` }} data-testid="baseline-guides" data-spacing={baselineGuide.spacing} data-offset={baselineGuide.offset} />
              )}
              {vo.guides !== false && lineGuides.map((guide) => (
                <GuideLine key={guide.id} guide={guide} zoom={state.zoom} pageWidth={currentPage.width} pageHeight={currentPage.height} />
              ))}
              <GuideDragOverlay zoom={state.zoom} pageWidth={currentPage.width} pageHeight={currentPage.height} />
              <ObjectDragOverlay zoom={state.zoom} pageWidth={currentPage.width} pageHeight={currentPage.height} />
              {alignmentGuides.map((guide, index) => (
                <div key={`align-${index}`} className="absolute pointer-events-none" data-testid="alignment-guide-line" data-type={guide.type} data-orientation={guide.orientation} data-position={guide.position} style={{ backgroundColor: guide.type === 'user-guide' ? '#22c55e' : guide.type === 'center' || guide.type === 'object-center' ? '#ff3366' : '#00d4ff', zIndex: 998, ...(guide.orientation === 'horizontal' ? { left: -50, right: -50, top: guide.position * state.zoom, height: 1 } : { top: -50, bottom: -50, left: guide.position * state.zoom, width: 1 }) }} />
              ))}

              <div className="absolute inset-0" style={{ overflow: 'visible' }}>
                {masterObjects.map((obj) => (
                  <CanvasObject key={`m-${obj.id}`} object={obj} isSelected={false} zoom={state.zoom} canvasWidth={currentPage.width} canvasHeight={currentPage.height} allObjects={[]} userGuides={[]} isMasterObject />
                ))}
                {activeObjects.map((obj) => (
                  <CanvasObject key={obj.id} object={obj} isSelected={state.selectedObjectIds.includes(obj.id)} zoom={state.zoom} onAlignmentGuides={setAlignmentGuides} canvasWidth={currentPage.width} canvasHeight={currentPage.height} allObjects={editableObjects} userGuides={lineGuides} isScratchObject={scratchIds.has(obj.id)} />
                ))}
              </div>

              <SampleColorOverlay page={isMasterView ? { ...currentPage, objects: master.objects, masterId: 'none' } : currentPage} master={pageMaster} />
              {wrapEditTarget && <WrapPointsEditor object={wrapEditTarget} zoom={state.zoom} canvasRef={canvasRef} />}
              {cropEditTarget && <CropEditor object={cropEditTarget} zoom={state.zoom} canvasRef={canvasRef} />}
              {pointEditTarget && <EditPointsEditor object={pointEditTarget} zoom={state.zoom} canvasRef={canvasRef} />}
              {miniToolbarTarget && <PictureMiniToolbar object={miniToolbarTarget} zoom={state.zoom} />}
              {story && vo.storyMap !== false && <StoryMapOverlay story={story} zoom={state.zoom} editingId={state.editingObjectId} visibleIds={renderedIds} />}

              {isDrawing && drawRect && (
                <div className="absolute border-2 border-dashed border-primary bg-primary/10 pointer-events-none" style={{ left: drawRect.x * state.zoom, top: drawRect.y * state.zoom, width: drawRect.width * state.zoom, height: drawRect.height * state.zoom }} />
              )}
              {marquee && Math.abs(marquee.x2 - marquee.x1) > 2 && (
                <div className="absolute border border-primary bg-primary/10 pointer-events-none" style={{ left: Math.min(marquee.x1, marquee.x2) * state.zoom, top: Math.min(marquee.y1, marquee.y2) * state.zoom, width: Math.abs(marquee.x2 - marquee.x1) * state.zoom, height: Math.abs(marquee.y2 - marquee.y1) * state.zoom, zIndex: 999 }} />
              )}
            </div>
            <div className="absolute text-[11px] text-muted-foreground" style={{ left: canvasLeft, top: PAD_TOP + canvasHeight + 6 }} data-testid="canvas-page-caption">{isMasterView ? `${master?.name || 'Master Page'} (master page)` : `${currentPage.name}${spread ? ' (editing)' : ''}`}</div>
            {/* joined sheet: the fold line down the middle, with fold marks just outside the sheet */}
            {joined && (
              <div className="absolute pointer-events-none" style={{ left: foldX, top: PAD_TOP - 14, height: canvasHeight + 28, width: 0, zIndex: 3 }} aria-hidden="true" data-testid="spread-fold-line">
                <div className="absolute left-0 top-0 h-full border-l border-dashed border-foreground/40" style={{ width: 0 }} />
                <div className="absolute -translate-x-1/2 left-0 h-[14px] w-px bg-foreground/60" style={{ top: 0 }} />
                <div className="absolute -translate-x-1/2 left-0 h-[14px] w-px bg-foreground/60" style={{ bottom: 0 }} />
                <div className="absolute -translate-x-1/2 left-0 text-[9px] uppercase tracking-wider text-muted-foreground select-none whitespace-nowrap" style={{ top: -14 }}>fold</div>
              </div>
            )}
          </div>
        </div>
        {story && vo.storyMap !== false && <StoryMapPanel story={story} textFlow={state.textFlow} offsetLeft={showRulers ? 36 : 12} />}
      </div>
    </div>
  );
};
